import React, { ReactElement, ReactNode, useState } from 'react'
import { Box, Card, Skeleton, Tab, Tabs, Typography } from '@mui/material'

import { useAppSelector } from 'src/store/hooks'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import {
  AccreditationKey,
  fetchCourseAccreditationHealthDashboard,
  fetchEventAccreditationHealthDashboard,
  normalizeCourseRows,
  normalizeEventRows,
  selectCourseAccreditationHealthDashboard,
  selectCourseAccreditationHealthDashboardStatus,
  selectEventAccreditationHealthDashboard,
  selectEventAccreditationHealthDashboardStatus,
} from 'src/v3/content-pipeline/accreditation-health.slice'
import AccreditationOverviewTab from './accreditation-overview-tab'
import AccreditationNeedsAttentionTab from './accreditation-needs-attention-tab'
import AccreditationDraftPipelineTab from './accreditation-draft-pipeline-tab'

type ContentDomain = 'live' | 'enduring'

type Props = {
  domain: ContentDomain
  onSelectRow: (id: string) => void
  children: ReactNode
}

function formatGeneratedAt(dateStr: string): string {
  return `${dateStr.replace('T', ' ').replace('Z', '')} UTC`
}

export default function AccreditationHealthDashboard({
  domain,
  onSelectRow,
  children,
}: Props): ReactElement {
  let [activeTab, setActiveTab] = useState(0)
  let [accreditationFilter, setAccreditationFilter] = useState<
    AccreditationKey | 'all'
  >('all')

  let courseDashboard = useAppSelector(selectCourseAccreditationHealthDashboard)
  let courseDashboardStatus = useAppSelector(
    selectCourseAccreditationHealthDashboardStatus,
  )
  let eventDashboard = useAppSelector(selectEventAccreditationHealthDashboard)
  let eventDashboardStatus = useAppSelector(
    selectEventAccreditationHealthDashboardStatus,
  )

  useAutoFetch(courseDashboardStatus, fetchCourseAccreditationHealthDashboard)
  useAutoFetch(eventDashboardStatus, fetchEventAccreditationHealthDashboard)

  let dashboard = domain === 'live' ? eventDashboard : courseDashboard
  let status = domain === 'live' ? eventDashboardStatus : courseDashboardStatus
  let noun: 'event' | 'course' = domain === 'live' ? 'event' : 'course'

  let goToRowDetail = (id: string) => {
    onSelectRow(id)
    setActiveTab(3)
  }

  let goToNeedsAttention = (key: AccreditationKey) => {
    setAccreditationFilter(key)
    setActiveTab(1)
  }

  if (status === 'pending' || status === 'uninitialized') {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Skeleton variant="text" sx={{ width: '40%', height: 32, mb: 2 }} />
        <Skeleton variant="rectangular" sx={{ height: 120 }} />
      </Card>
    )
  }

  if (status === 'rejected' || !dashboard) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3, bgcolor: 'error.dark' }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Error Loading Accreditation Health
        </Typography>
        <Typography color="text.secondary" variant="body2">
          Failed to load the accreditation health dashboard. Please try again.
        </Typography>
      </Card>
    )
  }

  let rows =
    domain === 'live'
      ? normalizeEventRows(eventDashboard?.events ?? [])
      : normalizeCourseRows(courseDashboard?.courses ?? [])

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          mb: 0.5,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Updated {formatGeneratedAt(dashboard.generatedAt)}
        </Typography>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, value) => setActiveTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Overview" />
        <Tab label="Needs Attention" />
        <Tab label="Draft Pipeline" />
        <Tab label={domain === 'live' ? 'By Event' : 'By Course'} />
      </Tabs>

      {activeTab === 0 && (
        <AccreditationOverviewTab
          rows={rows}
          overall={dashboard.overall}
          totalsByAccreditation={dashboard.totalsByAccreditation}
          approachingWindowMonths={dashboard.approachingWindowMonths}
          onViewAccreditation={goToNeedsAttention}
        />
      )}

      {activeTab === 1 && (
        <AccreditationNeedsAttentionTab
          rows={rows}
          domain={domain}
          noun={noun}
          accreditationFilter={accreditationFilter}
          onAccreditationFilterChange={setAccreditationFilter}
          onSelectRow={goToRowDetail}
        />
      )}

      {activeTab === 2 && (
        <AccreditationDraftPipelineTab
          rows={rows}
          onSelectRow={goToRowDetail}
        />
      )}

      {activeTab === 3 && children}
    </Box>
  )
}
